import axios from 'axios';
import { getLlmApiUrl } from '../config/config.js';
import i18n from '../i18n.js';

// 创建axios实例
const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true // 启用跨域Cookie
});

// 请求拦截器，添加token
api.interceptors.request.use(
  (config) => {
    // const token = localStorage.getItem('doctor_token');
    // if (token) {
    //   config.headers['Authorization'] = `Bearer ${token}`;
    // }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 获取待审核报告列表
export const getPendingReports = async (page = 1, pageSize = 10) => {
  try {
    const response = await api.get(`/reports?status=pending&page=${page}&pageSize=${pageSize}`);
    return response.data;
  } catch (error) {
    console.error('获取待审核报告失败', error);
    throw error;
  }
};

// 获取已审核报告列表
export const getReviewedReports = async (page = 1, pageSize = 10) => {
  try {
    const response = await api.get(`/reports?status=approved,rejected&page=${page}&pageSize=${pageSize}`);
    return response.data;
  } catch (error) {
    console.error('获取已审核报告失败', error);
    throw error;
  }
};

// 获取单个报告详情
export const getReportById = async (id) => {
  try {
    const response = await api.get(`/reports/${id}`);
    return response.data;
  } catch (error) {
    console.error(`获取报告ID${id}详情失败`, error);
    throw error;
  }
};

// 通过token获取已审核报告详情（给诊所使用）
export const getReportByIdWithToken = async (id, token) => {
  try {
    const response = await axios.get(`/api/v1/reports/${id}?token=${token}`);
    return response.data;
  } catch (error) {
    console.error(`获取报告ID${id}详情失败`, error);
    throw error;
  }
};

// 获取就诊信息
export const getVisitById = async (visitId) => {
  try {
    const response = await api.get(`/visits/${visitId}`);
    return response.data;
  } catch (error) {
    console.error(`获取就诊ID${visitId}详情失败`, error);
    throw error;
  }
};

// 获取患者信息
export const getPatientById = async (patientId) => {
  try {
    const response = await api.get(`/patients/${patientId}`);
    return response.data;
  } catch (error) {
    console.error(`获取患者ID${patientId}详情失败`, error);
    throw error;
  }
};

// 审核报告
export const reviewReport = async (id, data) => {
  try {
    const response = await api.post(`/reports/${id}/review`, data);
    return response.data;
  } catch (error) {
    console.error(`审核报告ID${id}失败`, error);
    throw error;
  }
};

// 更新报告内容
export const updateReportContent = async (id, content) => {
  try {
    const response = await api.patch(`/reports/${id}`, { content });
    return response.data;
  } catch (error) {
    console.error(`更新报告ID${id}内容失败`, error);
    throw error;
  }
};

// 格式化检查项结果为exam_results格式
const formatCheckResultsAsExamResults = (checksData) => {
  if (!checksData || checksData.length === 0) {
    return {};
  }

  const examResults = {};
  
  for (const check of checksData) {
    if (!check.metadata) {
      continue;
    }

    // 使用检查类型作为键名
    const checkTypeKey = check.check_type || 'unknown_check';
    
    try {
      // 解析metadata
      let metadata;
      if (typeof check.metadata === 'string') {
        // 尝试解析JSON字符串
        try {
          metadata = JSON.parse(check.metadata);
        } catch {
          // 如果解析失败，作为普通文本处理
          metadata = { result: check.metadata };
        }
      } else if (typeof check.metadata === 'object') {
        metadata = check.metadata;
      } else {
        continue;
      }

      // 如果metadata包含items数组（检验报告格式）
      if (metadata.items && Array.isArray(metadata.items)) {
        const formattedResults = {};
        
        // 添加基本信息
        if (metadata.check_type) formattedResults.check_type = metadata.check_type;
        if (metadata.source) formattedResults.source = metadata.source;
        if (metadata.check_time) formattedResults.check_time = metadata.check_time;
        
        // 处理检验项目
        metadata.items.forEach((item, index) => {
          const itemKey = item.name || `item_${index}`;
          const itemValue = {
            value: item.value,
            unit: item.unit || '',
            reference: item.reference || '',
            abnormal: item.abnormal || false
          };
          
          if (item.abnormal_type) {
            itemValue.abnormal_type = item.abnormal_type;
          }
          
          formattedResults[itemKey] = itemValue;
        });
        
        examResults[checkTypeKey] = formattedResults;
      } else {
        // 直接使用metadata对象
        examResults[checkTypeKey] = metadata;
      }
      
    } catch (error) {
      console.warn(`格式化检查项 ${checkTypeKey} 时出错:`, error);
      // fallback：使用原始数据
      examResults[checkTypeKey] = {
        raw_data: check.metadata,
        check_type: check.check_type
      };
    }
  }


  // const combinedMetadata = [];
  // combinedMetadata.push(`检查类型： ${check.check_type}`);
  // // 如果 metadata 是字符串，直接使用；如果是对象，转换为 JSON 字符串
  // let metadataString;
  // if (typeof check.metadata === 'string') {
  //   metadataString = check.metadata;
  // } else {
  //   metadataString = JSON.stringify(check.metadata);
  // }
  
  // combinedMetadata.push(`检查结果明细： ${metadataString}`);
  // combinedMetadata.push('-----------');

  // return combinedMetadata.join('\n');

  return examResults;
};

// 格式化被拒绝的待检查项为exam_results格式
const formatRejectedPendingChecksAsExamResults = (rejectedPendingChecks) => {
  if (!rejectedPendingChecks || rejectedPendingChecks.length === 0) {
    return {};
  }

  const examResults = {};
  
  for (const pendingCheck of rejectedPendingChecks) {
    // 使用检查类型作为键名，添加_rejected后缀以区分
    const checkTypeKey = `${pendingCheck.check_type || 'unknown_check'}_rejected`;
    
    examResults[checkTypeKey] = {
      check_type: pendingCheck.check_type,
      description: pendingCheck.description || '',
      status: 'rejected',
      rejected_reason: pendingCheck.note || i18n.t('reports.doctorRejectedThisCheck'),
      created_at: pendingCheck.created_at,
      reviewed_at: pendingCheck.reviewed_at,
      reviewer_name: pendingCheck.reviewer_name || ''
    };
  }

  return examResults;
};

// 生成报告内容
export const generateReportContent = async (conversationId, reportType = 'proposal', patientInfo = {}, checksData = null, rejectedPendingChecks = null) => {
  try {
    if (!conversationId) {
      throw new Error('conversationId 是必需的参数');
    }
    
    // 根据报告类型确定任务类型
    let taskType;
    if (reportType === 'proposal') {
      taskType = 'preliminary_record';
    } else if (reportType === 'final') {
      taskType = 'standard_record';
    } else {
      throw new Error(`不支持的报告类型: ${reportType}`);
    }
    
    // 构建请求数据
    const requestData = {
      conversation_id: conversationId,
      task_type: taskType,
      patient_info: patientInfo || {}, // 必需字段，提供默认空对象
      additional_info: "" // 提供默认值
    };
    
    // 处理检查项结果数据 - 放在exam_results字段下
    if (reportType === 'final' && checksData && checksData.length > 0) {
      const examResults = formatCheckResultsAsExamResults(checksData);
      if (Object.keys(examResults).length > 0) {
        requestData.exam_results = examResults;
      }
    }
    
    // 处理被拒绝的待检查项 - 也放在exam_results字段下
    if (reportType === 'final' && rejectedPendingChecks && rejectedPendingChecks.length > 0) {
      const rejectedExamResults = formatRejectedPendingChecksAsExamResults(rejectedPendingChecks);
      if (Object.keys(rejectedExamResults).length > 0) {
        console.log('格式化后的被拒绝待检查项:', rejectedExamResults);
        // 合并到现有的exam_results中
        if (!requestData.exam_results) {
          requestData.exam_results = {};
        }
        requestData.exam_results = {
          ...requestData.exam_results,
          ...rejectedExamResults
        };
      }
    }
    
    // 打印最终发送给后端的请求数据
    if (reportType === 'final' && requestData.exam_results) {
      console.log('发送给后端的exam_results:', JSON.stringify(requestData.exam_results, null, 2));
    }
    
    const response = await axios.post(getLlmApiUrl('/medical/records/generate'), requestData, {
      headers: {
        'Content-Type': 'application/json'
      },
      withCredentials: true
    });
    
    // 处理响应格式，根据提供的示例
    if (response.data && response.data.status === 'success') {
      if (response.data.content) {
        return response.data.content;
      } else {
        throw new Error('服务器返回成功但没有内容');
      }
    } else if (response.data && response.data.error) {
      throw new Error(`生成失败: ${response.data.error}`);
    } else {
      throw new Error('未能获取到报告内容: 响应格式不正确');
    }
  } catch (error) {
    console.error('生成报告内容失败', error);
    throw error;
  }
};

// 获取就诊检查项列表
export const getChecksByVisitId = async (visitId) => {
  try {
    // 使用支持医生权限的端点
    const response = await api.get(`/visits/${visitId}/checks`);
    return response.data;
  } catch (error) {
    console.error(`获取就诊ID${visitId}的检查项失败`, error);
    throw error;
  }
};

// 通过token获取就诊检查项列表（给诊所使用）
export const getChecksByVisitIdWithToken = async (visitId, token) => {
  try {
    const response = await axios.get(`/api/v1/visits/${visitId}/checks?token=${token}`);
    return response.data;
  } catch (error) {
    console.error(`获取就诊ID${visitId}的检查项失败`, error);
    throw error;
  }
};

// 根据就诊ID和报告类型获取报告列表
export const getReportsByVisitAndType = async (visitId, reportType) => {
  try {
    const response = await api.get(`/reports/visit/${visitId}/type/${reportType}`);
    return response.data;
  } catch (error) {
    console.error(`获取就诊ID${visitId}的${reportType}报告失败`, error);
    throw error;
  }
};

// 根据就诊ID和报告类型获取最新报告
export const getLatestReportByVisitAndType = async (visitId, reportType) => {
  try {
    const response = await api.get(`/reports/visit/${visitId}/type/${reportType}/latest`);
    return response.data;
  } catch (error) {
    console.error(`获取就诊ID${visitId}的最新${reportType}报告失败`, error);
    throw error;
  }
};

// 通过就诊ID获取对话消息列表
export const getConversationMessagesByVisitId = async (visitId) => {
  try {
    const response = await api.get(`/conversations/visit/${visitId}/messages`);
    return response.data;
  } catch (error) {
    console.error(`获取就诊ID${visitId}的对话消息失败`, error);
    throw error;
  }
};

// 通过对话ID获取对话消息列表
export const getConversationMessagesByConversationId = async (conversationId) => {
  try {
    const response = await api.get(`/conversations/${conversationId}/messages`);
    return response.data;
  } catch (error) {
    console.error(`获取对话ID${conversationId}的对话消息失败`, error);
    throw error;
  }
};

// 获取就诊待检查项列表
export const getPendingChecksByVisitId = async (visitId) => {
  try {
    const response = await api.get(`/visits/${visitId}/pending-checks`);
    return response.data;
  } catch (error) {
    console.error(`获取就诊ID${visitId}的待检查项失败`, error);
    throw error;
  }
};

// 创建待检查项目
export const createPendingCheck = async (data) => {
  try {
    const response = await api.post('/pending-checks', data);
    return response.data;
  } catch (error) {
    console.error('创建待检查项目失败', error);
    throw error;
  }
};

// 审核待检查项目
export const reviewPendingCheck = async (pendingCheckId, data) => {
  try {
    const response = await api.post(`/pending-checks/${pendingCheckId}/review`, data);
    return response.data;
  } catch (error) {
    console.error(`审核待检查项目ID${pendingCheckId}失败`, error);
    throw error;
  }
};

// 删除待检查项目
export const deletePendingCheck = async (pendingCheckId) => {
  try {
    const response = await api.delete(`/pending-checks/${pendingCheckId}`);
    return response.data;
  } catch (error) {
    console.error(`删除待检查项目ID${pendingCheckId}失败`, error);
    throw error;
  }
};

// 创建新报告
export const createReport = async (visitId, reportType = 'final', conversationId) => {
  try {
    const response = await api.post('/reports/create', {
      visit_id: visitId,
      report_type: reportType,
      conversation_id: conversationId
    });
    return response.data;
  } catch (error) {
    console.error(`创建报告失败`, error);
    throw error;
  }
};

// 提交检查结果 - 参考ReportSubmissionView.swift的submitCustomItems方法
export const submitCheckResult = async (data) => {
  try {
    // 构建请求数据，参考ReportSubmissionView.swift中的提交格式
    const requestData = {
      check_type: data.type,
      blob: data.blob,
      metadata: data.metadata,
      blob_mime_type: data.blobType,
      blob_type: data.blobType, // 添加缺少的blob_type字段
      pending_check_id: data.pendingCheckId
    };

    // 如果提供了visitId，添加到请求数据中
    if (data.visitId) {
      requestData.visit_id = data.visitId;
    }

    const response = await api.post('/checks', requestData);
    return response.data;
  } catch (error) {
    console.error('提交检查结果失败', error);
    throw error;
  }
};

// 获取复诊预约单列表
export const getAppointmentOrders = async (params = {}) => {
  try {
    const response = await api.get('/appointment-orders', { params });
    return response.data;
  } catch (error) {
    console.error('获取预约单列表失败', error);
    throw error;
  }
};

// 基于已审核的 final report 追加创建预约单
export const createReportAppointmentOrders = async (reportId, appointmentOrders) => {
  try {
    const response = await api.post(`/reports/${reportId}/appointment-orders`, {
      appointment_orders: appointmentOrders
    });
    return response.data;
  } catch (error) {
    console.error(`为报告ID${reportId}创建预约单失败`, error);
    throw error;
  }
};

// 取消预约单
export const cancelAppointmentOrder = async (id) => {
  try {
    const response = await api.post(`/appointment-orders/${id}/cancel`);
    return response.data;
  } catch (error) {
    console.error(`取消预约单ID${id}失败`, error);
    throw error;
  }
};

// 重置预约单
export const resetAppointmentOrder = async (id) => {
  try {
    const response = await api.post(`/appointment-orders/${id}/reset`);
    return response.data;
  } catch (error) {
    console.error(`重置预约单ID${id}失败`, error);
    throw error;
  }
};
